import React, { useEffect, useRef, useState } from "react";
import { Loader2, ScanText, X } from "lucide-react";
import { toast } from "sonner";
import { formatErr } from "@/lib/api";
import { dapi as api } from "./deskApi";
import { AreaPicker, PicturePicker } from "./pdfAiPickers";
import { Preview, WordRow, startEdits, useChangedCount } from "./pdfAiEditor";

const errText = (e) => (e.response ? formatErr(e.response.data?.detail) : "The connection dropped. Please try again.");

const Editor = ({ result, onBack, onApply, busy }) => {
  const [base] = useState(() => startEdits(result.words));
  const [edits, setEdits] = useState(base);
  const [selected, setSelected] = useState(result.words[0]?.id || null);
  const canvasRef = useRef(null);
  const count = useChangedCount(result.words, edits, base);
  return (
    <div className="grid md:grid-cols-[1fr_300px] gap-3 min-h-0" data-testid="pdf-ai-editor">
      <div className="overflow-auto max-h-[62vh] rounded border border-slate-200 p-2 grid place-items-center">
        <Preview src={result.image} words={result.words} edits={edits} base={base} selected={selected} onSelect={setSelected} canvasRef={canvasRef} />
      </div>
      <div className="flex flex-col min-h-0 max-h-[62vh]">
        <p className="text-xs text-slate-500 mb-2">{result.words.length ? `The AI found ${result.words.length} text ${result.words.length === 1 ? "piece" : "pieces"}. Change the words, then Apply.` : "The AI didn't find any words here."}</p>
        <div className="space-y-2 overflow-y-auto flex-1 pr-1">
          {result.words.map((w) => (
            <WordRow key={w.id} word={w} edit={edits[w.id]} selected={selected === w.id} onSelect={() => setSelected(w.id)}
              onChange={(e) => setEdits((p) => ({ ...p, [w.id]: e }))} />
          ))}
        </div>
        <div className="flex gap-2 pt-2">
          <button type="button" onClick={onBack} className="flex-1 rounded border border-slate-300 py-1.5 text-xs" data-testid="pdf-ai-back">Back</button>
          <button type="button" disabled={!count || busy} onClick={() => onApply(canvasRef.current.toDataURL("image/png"))}
            className="flex-1 rounded bg-sky-600 text-white py-1.5 text-xs font-semibold disabled:opacity-40" data-testid="pdf-ai-apply">
            {busy ? "Saving…" : `Apply${count ? ` (${count})` : ""}`}
          </button>
        </div>
      </div>
    </div>
  );
};

// "Edit image text with AI" for the PDF open in PdfCraft. Our own code, so PdfCraft updates keep it.
export default function PdfImageText({ file, onClose, onSaved }) {
  const [scan, setScan] = useState(null);
  const [tab, setTab] = useState("pictures");
  const [target, setTarget] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`/desktop/fs/${file.id}/pdf-images`).then(({ data }) => setScan(data)).catch((e) => { toast.error(errText(e)); onClose(); });
  }, [file.id, onClose]);
  const analyze = async (t) => {
    setTarget(t); setResult(null); setBusy(true);
    try { setResult((await api.post(`/desktop/fs/${file.id}/pdf-image-text/analyze`, t, { timeout: 180000 })).data); }
    catch (e) { toast.error(errText(e)); setTarget(null); }
    finally { setBusy(false); }
  };
  const apply = async (image) => {
    setBusy(true);
    try {
      await api.post(`/desktop/fs/${file.id}/pdf-image-text/apply`, { ...target, image }, { timeout: 300000 });
      toast.success(`Updated "${file.name}". Restore previous version can undo it.`);
      window.dispatchEvent(new CustomEvent("desk-fs-changed"));
      onSaved();
    } catch (e) { toast.error(errText(e)); setBusy(false); }
  };
  const tabBtn = (k, label) => (
    <button type="button" onClick={() => setTab(k)} className={`px-3 py-1 text-xs rounded-full ${tab === k ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700"}`} data-testid={`pdf-ai-tab-${k}`}>{label}</button>
  );
  return (
    <div className="absolute inset-0 z-20 bg-slate-900/50 p-3 grid place-items-center" data-testid="pdf-ai-dialog">
      <div className="w-full max-w-5xl max-h-full overflow-hidden rounded-lg bg-white shadow-2xl p-4 flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <ScanText className="w-5 h-5 text-sky-600" />
          <div className="font-semibold text-slate-900 text-sm">Edit image text with AI</div>
          <button type="button" onClick={onClose} className="ml-auto p-1 rounded hover:bg-slate-100" data-testid="pdf-ai-close"><X className="w-4 h-4" /></button>
        </div>
        {!target && <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">Save your PdfCraft changes first — this edits the saved PDF and then reopens it.</p>}
        {busy && !result && <div className="grid place-items-center py-16 text-sm text-slate-500 gap-2" data-testid="pdf-ai-reading"><Loader2 className="w-6 h-6 animate-spin" />Reading the words…</div>}
        {!target && !busy && (!scan ? <Loader2 className="w-6 h-6 animate-spin text-slate-400 mx-auto my-10" /> : <>
          <div className="flex gap-2">{tabBtn("pictures", "Pictures")}{tabBtn("area", "Page area")}</div>
          <div className="overflow-y-auto max-h-[65vh]">
            {tab === "pictures" ? <PicturePicker images={scan.images} onPick={analyze} /> : <AreaPicker nid={file.id} pages={scan.pages} onPick={analyze} />}
          </div>
        </>)}
        {result && <Editor result={result} busy={busy} onBack={() => { setTarget(null); setResult(null); }} onApply={apply} />}
      </div>
    </div>
  );
}
